import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { viewAsProId } from '@/lib/viewAs';
import { CELL_DOOR_OFF, ownerHubDoorOrigin, ownerHubToken } from '@/lib/cell/ownerHub';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { useFeature } from '@/lib/useFeature';

/**
 * REVIEWS — the pro's OWN Google reviews (1A rebuild).
 *
 * Peter, 2026-08-28: "you got the reviews all wrong — what you are showing
 * is Sharon's review... connect to the pros google and show them their own
 * review. This is the pro surface, not owners." The first cut put the
 * STUDIO's listing here; this room is the pro's independent business —
 * THEIR listing, THEIR rating, THEIR reviews. The studio's reviews appear
 * nowhere on this surface.
 *
 * Three states:
 *   · not linked  → find-your-listing search, prefilled with their business
 *     name, plus the honest no-listing coaching (creating one IS the growth
 *     move for half the roster);
 *   · linked      → their rating hero + Google's five newest reviews (the
 *     lookup API's ceiling — said on screen, never hidden), with unlink;
 *   · dark        → the switchboard fence, same posture as Grow.
 *
 * READ-ONLY still: replying in their own voice arrives with 1B's Google
 * connect, where Google itself proves the listing is theirs.
 */

// CELL (M28): the Owner Hub door for this build — doorOrigin() in production (lib/hosts.ts), the AWS staging host on
// the cell build; null there = not on the AWS staging build yet, and the door helper below says so (CELL_DOOR_OFF).
const DOOR = ownerHubDoorOrigin();

type Candidate = { place_id: string; name: string; address: string; rating: number | null; count: number | null };
type Listing = {
  place_id: string; name: string | null; address: string | null;
  rating: number | null; count: number | null; syncedAt: string | null;
  reviews: { reviewer: string; rating: number; text: string; when: string }[];
};
type DoorPayload = {
  ok: boolean; error?: string;
  connected: boolean; businessName?: string | null; listing?: Listing;
};

async function doorFetch(path: string, init?: RequestInit): Promise<DoorPayload & { candidates?: Candidate[] }> {
  if (!DOOR) throw new Error(CELL_DOOR_OFF);
  // CELL (M28): this tab's own token — Supabase's in production, Cognito's access token on the cell build (lib/cell/ownerHub.ts).
  const token = await ownerHubToken();
  if (!token) throw new Error('Sign in again — no session.');
  const res = await fetch(`${DOOR}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, ...(init?.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.ok) throw new Error(body?.error ?? 'Could not reach your reviews right now.');
  return body;
}

export default function Reviews() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const { activeLocationId } = useActiveLocationId();
  const feature = useFeature('reviews');
  const queryClient = useQueryClient();
  const viewing = viewAsProId();

  const { data, isLoading, error } = useQuery<DoorPayload>({
    queryKey: ['pro-own-reviews', userId, activeLocationId, viewing],
    enabled: !!userId && !!activeLocationId && feature.enabled,
    // Under an operator's View-their-hub lens the door is asked about THAT pro (the #99 rule:
    // every door a lensed surface calls must carry the lens), read only.
    queryFn: () => doorFetch(`/pro/reviews?location_id=${encodeURIComponent(activeLocationId!)}${viewing ? `&view_pro=${encodeURIComponent(viewing)}` : ''}`),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['pro-own-reviews'] });

  // The fence: a direct URL to a dark feature says so honestly — Grow's posture.
  if (!feature.loading && !feature.enabled) {
    return (
      <div className="card text-sm text-image-charcoal">
        This section isn&rsquo;t switched on for your studio yet.
      </div>
    );
  }
  if (feature.loading || isLoading) {
    return <div className="text-image-slate text-sm">Loading…</div>;
  }
  if (error || !data) {
    return (
      <div className="card text-sm text-image-charcoal">
        Couldn&rsquo;t load your reviews right now — try again in a minute.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-image-black">
          Your <span className="text-image-pink">Reviews</span>
        </h1>
        <p className="text-sm text-image-charcoal mt-1">
          Your business, your listing, your reputation.
        </p>
      </div>

      {/* Under the owner's lens the room is READ ONLY: no unlink, no search — linking a listing
          is the pro's own act, and the door refuses every op from a viewer anyway. */}
      {data.connected && data.listing
        ? <LinkedListing listing={data.listing} locationId={activeLocationId!} onChanged={refresh} readOnly={!!viewing} />
        : viewing
          ? (
            <div className="card text-sm text-image-charcoal">
              No listing linked yet. Only the pro can find and confirm their own listing — you are
              viewing this as the studio.
            </div>
          )
          : <FindListing businessName={data.businessName ?? ''} locationId={activeLocationId!} onLinked={refresh} />}
    </div>
  );
}

function LinkedListing({ listing, locationId, onChanged, readOnly = false }: {
  readOnly?: boolean;
  listing: Listing; locationId: string; onChanged: () => void;
}) {
  const [unlinking, setUnlinking] = useState(false);
  // HUB-060: a refused or dropped unlink used to escape as an unhandled rejection and say nothing.
  const [unlinkErr, setUnlinkErr] = useState('');
  const unlink = async () => {
    setUnlinking(true); setUnlinkErr('');
    try {
      await doorFetch('/pro/reviews', { method: 'POST', body: JSON.stringify({ op: 'clear', location_id: locationId }) });
      onChanged();
    } catch (e) {
      setUnlinkErr(`It is still linked — ${e instanceof Error ? e.message : 'the unlink did not go through'}. Try again.`);
    } finally { setUnlinking(false); }
  };

  return (
    <>
      <div className="rounded-2xl bg-image-black text-white p-5 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-image-pink/30 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="text-[10px] font-extrabold tracking-[0.14em] uppercase text-white/50">
            {listing.name ?? 'Your listing'}
          </div>
          <div className="flex items-end gap-3 mt-1">
            <div className="font-display text-5xl font-extrabold text-image-pink">
              {listing.rating != null ? listing.rating.toFixed(1) : '—'}
            </div>
            <div className="pb-1.5">
              {listing.rating != null && (
                <div className="text-sm font-semibold" aria-label={`${listing.rating.toFixed(1)} out of 5 stars`}>
                  {'★'.repeat(Math.round(listing.rating))}<span className="text-white/30">{'★'.repeat(5 - Math.round(listing.rating))}</span>
                </div>
              )}
              <div className="text-xs text-white/60">
                {listing.count ?? 0} Google review{listing.count === 1 ? '' : 's'} · your own listing
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* The listing checks used to sit here. They now live at Grow › Check my listing, which
          is where the approved design put them — this page is about the reviews themselves. */}
      <section className="space-y-2">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.15em] text-image-charcoal px-1">
          Your latest reviews
        </h2>
        {listing.reviews.length > 0 ? (
          listing.reviews.map((r, i) => (
            <article key={i} className="rounded-2xl border border-image-border bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold text-sm text-image-black">{r.reviewer}</div>
                <div className="text-[12px] text-image-pink font-semibold shrink-0" aria-label={`${r.rating} out of 5 stars`}>
                  {'★'.repeat(Math.max(0, Math.min(5, r.rating)))}
                </div>
              </div>
              {r.text && <p className="text-sm text-image-dark-charcoal mt-1.5 leading-relaxed">{r.text}</p>}
            </article>
          ))
        ) : (
          <div className="card text-sm text-image-charcoal">
            No reviews on your listing yet — ask your next happy client. It takes them a minute
            and it books you for months.
          </div>
        )}
        <p className="text-xs text-image-charcoal px-1">
          Google shares your five newest reviews here. Connecting your Google account — coming
          next — unlocks your full history and lets you reply from this room.
        </p>
      </section>

      <div className="card flex items-center justify-between gap-3">
        <div className="text-xs text-image-charcoal min-w-0">
          Linked to <b className="text-image-black">{listing.name ?? 'your listing'}</b>
          {listing.address ? <> · {listing.address}</> : null}
        </div>
        {!readOnly && (
          <button type="button" onClick={unlink} disabled={unlinking}
            className="shrink-0 text-xs font-bold text-image-charcoal hover:text-image-pink transition-colors">
            {unlinking ? 'Unlinking…' : 'Not yours? Unlink'}
          </button>
        )}
      </div>
      {unlinkErr && <p role="alert" className="text-xs text-red-700 mt-2" data-testid="unlink-error">{unlinkErr}</p>}
    </>
  );
}

function FindListing({ businessName, locationId, onLinked }: {
  businessName: string; locationId: string; onLinked: () => void;
}) {
  const [query, setQuery] = useState(businessName);
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const search = async () => {
    if (!query.trim()) return;
    setBusy(true); setFailed(null);
    try {
      const out = await doorFetch('/pro/reviews', {
        method: 'POST',
        body: JSON.stringify({ op: 'lookup', location_id: locationId, query: query.trim() }),
      });
      setCandidates(out.candidates ?? []);
    } catch (e) {
      setFailed(e instanceof Error ? e.message : 'Search failed — try again.');
    } finally { setBusy(false); }
  };

  const confirm = async (c: Candidate) => {
    setBusy(true); setFailed(null);
    try {
      await doorFetch('/pro/reviews', {
        method: 'POST',
        body: JSON.stringify({ op: 'confirm', location_id: locationId, place_id: c.place_id, name: c.name, address: c.address }),
      });
      onLinked();
    } catch (e) {
      setFailed(e instanceof Error ? e.message : 'Could not link that listing — try again.');
      setBusy(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="text-[10px] font-extrabold tracking-[0.14em] text-image-pink uppercase">
          Find your listing
        </div>
        <h2 className="font-display font-extrabold text-lg mt-0.5">Your business is on Google — claim it here.</h2>
        <p className="text-xs text-image-charcoal mt-1">
          Search for your business the way a client would, then confirm it&rsquo;s yours. Your
          reviews live on <em>your</em> listing — not the studio&rsquo;s.
        </p>
        <div className="mt-3 flex gap-2">
          <input
            className="input flex-1 text-base"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
            placeholder="Your business name"
            aria-label="Your business name"
          />
          <button type="button" onClick={search} disabled={busy || !query.trim()}
            className="shrink-0 bg-image-pink text-white text-sm font-extrabold rounded-xl px-4 disabled:opacity-40">
            {busy ? 'Searching…' : 'Search'}
          </button>
        </div>
        {failed && <p className="text-xs text-red-600 mt-2">{failed}</p>}
        {candidates && (
          <div className="mt-3 space-y-2">
            {candidates.length === 0 && (
              <p className="text-sm text-image-charcoal">
                Nothing came up under that name — try adding your town, or see below if you
                haven&rsquo;t created a listing yet.
              </p>
            )}
            {candidates.map((c) => (
              <div key={c.place_id} className="rounded-xl border border-image-border p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-image-black truncate">{c.name}</div>
                  <div className="text-xs text-image-charcoal truncate">{c.address}</div>
                  {c.rating != null && (
                    <div className="text-xs text-image-pink font-semibold mt-0.5">
                      {c.rating.toFixed(1)} ★ · {c.count ?? 0} reviews
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => confirm(c)} disabled={busy}
                  className="shrink-0 bg-image-pink text-white text-xs font-extrabold rounded-full px-3.5 py-2 disabled:opacity-40">
                  This is me
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* The no-listing half — for many pros this coaching IS the feature. */}
      <div className="card">
        <div className="text-[10px] font-extrabold tracking-[0.14em] text-image-pink uppercase">
          No listing yet?
        </div>
        <h2 className="font-display font-extrabold text-lg mt-0.5">Ten minutes, and clients can find you.</h2>
        <p className="text-xs text-image-charcoal mt-1">
          A free Google Business Profile puts your business — not just your studio — on the map
          and in search. It&rsquo;s where your reviews will live.
        </p>
        <ol className="mt-2 space-y-1.5 list-decimal list-inside text-sm text-image-dark-charcoal">
          <li>Create it free at <a className="font-bold text-image-pink hover:underline" href="https://business.google.com" target="_blank" rel="noreferrer">business.google.com</a> — use your business name, not your own.</li>
          <li>Set your address to the studio and your service area to the towns you serve.</li>
          <li>Come back here, search your name, and tap <em>This is me</em>.</li>
        </ol>
      </div>
    </>
  );
}
